import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import Navbar from "@/components/Navbar";
import { Badge } from "@/components/ui/badge";
import { Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Dating", "Social", "Professional", "Relationship"];

export default function Practice() {
  const [scenarios, setScenarios] = useState([]);
  const [presc, setPresc] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/practice/scenarios").then((r) => setScenarios(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/prescriptions/current").then((r) => setPresc(r.data.prescription)).catch(() => {}); }, []);

  const open = (s) => {
    if (s.locked) { toast.error(`Practise ${s.unlock_after} conversations to unlock ${s.title}.`); return; }
    navigate(`/practice/${s.id}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">The Practice Room</p>
          <h1 className="font-serif text-4xl sm:text-5xl">Rehearse. Not everyone will love you.</h1>
          <p className="text-muted-foreground mt-2 max-w-xl">These people behave like real people — some warm, some distracted, some hard to win over. That's the point: your worth doesn't depend on their response.</p>
        </motion.div>

        {presc && presc.skill_targeted && (
          <motion.button initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onClick={() => navigate("/practice/continue")} data-testid="continue-work-btn"
            className="group w-full text-left rounded-2xl border border-secondary/40 bg-secondary/[0.06] p-6 mb-4 flex items-center justify-between hover:bg-secondary/[0.1] transition-colors duration-300">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-secondary mb-1">Continue your work</div>
              <h2 className="font-serif text-2xl">Let's see how {presc.skill_targeted.toLowerCase()} feels now</h2>
              <p className="text-sm text-muted-foreground mt-1">A new situation that quietly tests what we've been working on.</p>
            </div>
            <ArrowRight className="w-6 h-6 text-secondary shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
          </motion.button>
        )}

        <motion.button initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onClick={() => navigate("/practice/meet")} data-testid="meet-someone-btn"
          className="group w-full text-left rounded-2xl border border-primary/30 bg-primary/[0.04] p-6 mb-12 flex items-center justify-between hover:bg-primary/[0.07] transition-colors duration-300">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-primary mb-1">Chosen for you</div>
            <h2 className="font-serif text-2xl">Meet someone Xcelio created to test your pattern</h2>
            <p className="text-sm text-muted-foreground mt-1">There's a reason behind who you'll meet.</p>
          </div>
          <ArrowRight className="w-6 h-6 text-primary shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
        </motion.button>

        {CATS.map((cat) => {
          const items = scenarios.filter((s) => s.category === cat);
          if (!items.length) return null;
          return (
            <section key={cat} className="mb-12">
              <h2 className="font-serif text-2xl mb-4">{cat}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((s, i) => (
                  <motion.button key={s.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    onClick={() => open(s)} data-testid={`scenario-${s.id}`}
                    className={`group text-left rounded-2xl border border-border p-6 transition-all duration-300 ${s.locked ? "opacity-60" : "hover:border-primary/40 hover:-translate-y-1"}`}>
                    <div className="flex items-center justify-between mb-6">
                      <Badge variant="secondary" className="text-xs">{s.difficulty}</Badge>
                      {s.locked && <Lock className="w-4 h-4 text-muted-foreground" />}
                    </div>
                    <h3 className="font-serif text-2xl mb-1">{s.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">{s.tagline}</p>
                    <span className="inline-flex items-center gap-1 text-sm text-primary">
                      {s.locked ? `Unlocks at ${s.unlock_after} convos` : <>Begin <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" /></>}
                    </span>
                  </motion.button>
                ))}
              </div>
            </section>
          );
        })}
      </main>
    </div>
  );
}
